import type { ReactNode } from "react";
import { Settings2 } from "lucide-react";
import { Tooltip, HelpLabel } from "@/components/Wizard";
import { InfoPopover, type LearnMore } from "@/components/InfoPopover";

export { Tooltip, HelpLabel, InfoPopover };
export type { LearnMore };

export function PageHeader({
  title, subtitle, help, learnMore, actions,
}: {
  title: string;
  subtitle?: string;
  help?: ReactNode;
  learnMore?: LearnMore | LearnMore[];
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-3 border-t border-border pt-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-medium tracking-tight">
          {title}
          {(help || learnMore) && (
            <InfoPopover title={title} learnMore={learnMore}>{help}</InfoPopover>
          )}
        </h1>
        {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="card text-center text-muted">
      <div className="text-sm">{title}</div>
      {hint && <div className="mt-1 text-xs">{hint}</div>}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  const status = (error as Error & { status?: number })?.status;

  if (status === 403) {
    return <div className="card border-bad/40 text-sm text-bad" role="alert">
      <p className="font-medium">You don't have permission for this action.</p>
      <p className="mt-1">{msg}</p>
    </div>;
  }

  // 503 with "migration pending" context — show a distinct banner
  if (status === 503 || msg.toLowerCase().includes("migration")) {
    return (
      <div className="card border-warn/40 bg-warn/5 text-sm">
        <div className="flex items-start gap-2">
          <Settings2 size={16} className="mt-0.5 shrink-0 text-warn" />
          <div>
            <p className="font-medium text-warn">Setup in progress</p>
            <p className="mt-0.5 text-muted">{msg}</p>
          </div>
        </div>
      </div>
    );
  }

  return <div className="card border-bad/40 text-sm text-bad">Error: {msg}</div>;
}

export function StatusPill({ status }: { status?: string | null }) {
  const s = (status ?? "").toLowerCase();
  const cls =
    s === "succeeded" || s === "ok" || s === "active"   ? "pill-good" :
    s === "failed"    || s === "error"                  ? "pill-bad"  :
    s === "running"   || s === "pending" || s === "draft" ? "pill-warn"
                                                          : "pill";
  return <span className={cls}>{status ?? "—"}</span>;
}

interface Col<T> { header: string; cell: (row: T) => ReactNode; width?: string }
export function DataTable<T extends { id?: string }>({ rows, cols, empty, rowClassName }: {
  rows: T[]; cols: Col<T>[]; empty?: string; rowClassName?: (row: T) => string | undefined;
}) {
  if (!rows || rows.length === 0)
    return <EmptyState title={empty ?? "No rows yet."} />;
  return (
    <div className="card overflow-x-auto overflow-y-visible p-0">
      <table className="min-w-full text-sm">
        <thead className="bg-border/30 text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            {cols.map((c, i) => (
              <th key={i} className="px-3 py-2 font-medium" style={{ width: c.width }}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id ?? i} className={["border-t border-border/60", rowClassName?.(r) ?? ""].join(" ").trim()}>
              {cols.map((c, j) => (
                <td key={j} className="px-3 py-2 align-top">{c.cell(r)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
